import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  FileText,
  Loader2,
  Mail,
  Phone,
  Send,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { formatPhone } from '@/lib/utils/formatters'
import { cn } from '@/lib/utils/helpers'
import type { OutstandingBalanceRow } from '../../types/api'
import styles from './SendInvoiceModal.module.css'

export type SendInvoiceSubmitData = {
  /** Whether to send via email (Angular checkbox → query param `email`). */
  email: boolean
  /** Whether to send via phone/SMS (Angular checkbox control name `message`). */
  message: boolean
}

function ChannelCard({
  checked,
  onChange,
  label,
  value,
  icon,
  ariaLabel,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  value: string
  icon: ReactNode
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(styles.channelCard, checked && styles.channelCardSelected)}
    >
      <span
        className={cn(styles.checkbox, checked && styles.checkboxChecked)}
        aria-hidden
      >
        {checked ? (
          <svg viewBox="0 0 12 12" className={styles.checkIcon}>
            <path
              d="M2.5 6.2 5 8.7 9.5 3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      <span className={styles.channelIcon} aria-hidden>
        {icon}
      </span>
      <span className={styles.channelText}>
        <span className={styles.channelLabel}>{label}</span>
        <span className={styles.channelValue}>{value || '—'}</span>
      </span>
    </button>
  )
}

export function SendInvoiceModal({
  row,
  sending,
  onClose,
  onSubmit,
}: {
  row: OutstandingBalanceRow
  sending: boolean
  onClose: () => void
  onSubmit: (data: SendInvoiceSubmitData) => void
}) {
  useBodyScrollLock(true)

  const [sendEmail, setSendEmail] = useState(false)
  const [sendPhone, setSendPhone] = useState(false)

  const email = String(row.email ?? '').trim()
  const phoneRaw = String(row.mobile_no ?? '').trim()
  const phoneDisplay = phoneRaw ? formatPhone(phoneRaw) || phoneRaw : ''

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-invoice-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerLead}>
            <span className={styles.headerIconWrap} aria-hidden>
              <FileText className={styles.headerDocIcon} strokeWidth={1.75} />
              <span className={styles.headerSendBadge}>
                <Send className={styles.headerSendIcon} strokeWidth={2.25} />
              </span>
            </span>
            <div className={styles.headerCopy}>
              <h2 id="send-invoice-title" className={styles.title}>
                Send Invoice
              </h2>
              <p className={styles.subtitle}>
                Choose how you want to send this invoice
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </header>

        <div className={styles.body}>
          <h3 className={styles.sectionTitle}>
            <span>Send Invoice to</span>
            <span className={styles.sectionAccent} aria-hidden />
          </h3>

          <div className={styles.channelList}>
            <ChannelCard
              checked={sendEmail}
              onChange={setSendEmail}
              label="Email"
              value={email}
              icon={<Mail className="h-4 w-4" strokeWidth={2} />}
              ariaLabel={email ? `Email: ${email}` : 'Email'}
            />
            <ChannelCard
              checked={sendPhone}
              onChange={setSendPhone}
              label="Phone"
              value={phoneDisplay}
              icon={<Phone className="h-4 w-4" strokeWidth={2} />}
              ariaLabel={phoneDisplay ? `Phone: ${phoneDisplay}` : 'Phone'}
            />
          </div>
        </div>

        <footer className={styles.footer}>
          <p className={styles.secureNote}>
            <ShieldCheck className={styles.secureIcon} strokeWidth={2} aria-hidden />
            <span>Your invoice will be sent securely</span>
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.sendBtn}
              disabled={sending}
              onClick={() => onSubmit({ email: sendEmail, message: sendPhone })}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Send className="h-4 w-4" strokeWidth={2.25} aria-hidden />
              )}
              Send
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  )
}
